import api from './api';

// Cache keys (using sessionStorage for session-only cache)
const CACHE_KEYS = {
  SETTINGS: 'admin_shipping_settings',
  SETTINGS_TIMESTAMP: 'admin_shipping_settings_timestamp'
};

/**
 * Admin Settings Service - CRUD operations for site settings
 */
const getSettings = async (params = {}) => {
  return api.get('/admin/settings/', { params });
};

const getSettingById = async (id) => {
  return api.get(`/admin/settings/${id}/`);
};

const createSetting = async (data) => {
  return api.post('/admin/settings/', data);
};

const updateSetting = async (id, data) => {
  return api.put(`/admin/settings/${id}/`, data);
};

const deleteSetting = async (id) => {
  return api.delete(`/admin/settings/${id}/`);
};

/**
 * Public Settings Service - Fetch public settings (no auth required)
 */

/**
 * Fetch settings from the backend API
 * @returns {Promise<Object>} Settings object
 */
const fetchSettingsFromAPI = async () => {
  try {
    const response = await api.get('/admin/settings/public/multiple/', {
      params: {
        keys: 'initial_shipping_fee,minimum_service_fee,service_fee_percentage,price_display_mode,pricing_tier,area_id'
      }
    });
    
    const data = response.data;
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch settings');
    }
    
    // Extract the specific settings we need
    const settings = data.data || {};
    
    return {
      initial_shipping_fee: parseFloat(settings.initial_shipping_fee),
      minimum_service_fee: parseFloat(settings.minimum_service_fee),
      service_fee_percentage: parseFloat(settings.service_fee_percentage),
      price_display_mode: String(settings.price_display_mode || 'separated').trim().toLowerCase(),
      pricing_tier: settings.pricing_tier || [],
      area_id: settings.area_id
    };
  } catch (error) {
    console.error('Error fetching settings from API:', error);
    throw error;
  }
};

/**
 * Save settings to sessionStorage with timestamp
 * @param {Object} settings - Settings object to cache
 */
const saveSettingsToCache = (settings) => {
  try {
    const timestamp = Date.now();
    sessionStorage.setItem(CACHE_KEYS.SETTINGS, JSON.stringify(settings));
    sessionStorage.setItem(CACHE_KEYS.SETTINGS_TIMESTAMP, timestamp.toString());
  } catch (error) {
    console.error('Error saving settings to cache:', error);
  }
};

/**
 * Load settings from sessionStorage
 * Cache is valid for the entire session (until browser tab is closed)
 * @returns {Object|null} Cached settings or null if not found
 */
const loadSettingsFromCache = () => {
  try {
    const settings = sessionStorage.getItem(CACHE_KEYS.SETTINGS);
    
    if (!settings) {
      return null;
    }
    
    return JSON.parse(settings);
  } catch (error) {
    console.error('Error loading settings from cache:', error);
    return null;
  }
};

const hasRequiredShippingSettings = (settings) => {
  if (!settings || typeof settings !== 'object') return false;
  const hasLegacyFields = settings.initial_shipping_fee !== undefined &&
    settings.minimum_service_fee !== undefined &&
    settings.service_fee_percentage !== undefined &&
    settings.area_id !== undefined;
  const hasPricingModeFields = settings.price_display_mode !== undefined &&
    settings.pricing_tier !== undefined;
  return hasLegacyFields && hasPricingModeFields;
};

/**
 * Fetch public settings from the backend
 * @param {string|Array<string>} keys - Setting keys to fetch (comma-separated string or array)
 * @returns {Promise<Object>} Settings object with requested keys
 */
const getPublicSettings = async (keys) => {
  try {
    // Convert array to comma-separated string if needed
    const keysParam = Array.isArray(keys) ? keys.join(',') : keys;
    
    const response = await api.get('/admin/settings/public/multiple/', {
      params: {
        keys: keysParam
      }
    });
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'Failed to fetch settings');
    }
  } catch (error) {
    console.error('Error fetching public settings:', error);
    throw error;
  }
};

/**
 * Get shipping and service fee settings - tries cache first, then API
 * Cache is valid for the entire session
 * @returns {Promise<Object>} Settings object with:
 *   - initial_shipping_fee
 *   - minimum_service_fee
 *   - service_fee_percentage
 *   - area_id
 */
const getShippingSettings = async (forceRefresh = false) => {
  // Try to load from cache first
  const cachedSettings = loadSettingsFromCache();
  
  if (!forceRefresh && hasRequiredShippingSettings(cachedSettings)) {
    return cachedSettings;
  }
  
  // Cache miss, fetch from API
  const apiSettings = await fetchSettingsFromAPI();
  
  // Cache the new settings
  saveSettingsToCache(apiSettings);
  
  return apiSettings;
};

const settingsService = {
  // Admin CRUD operations
  getSettings,
  getSettingById,
  createSetting,
  updateSetting,
  deleteSetting,
  // Public settings operations
  getPublicSettings,
  getShippingSettings
};

export default settingsService;
export { settingsService };
